// Shared formatting helpers for the Assignment Pipeline's tab components.
// AssignmentPipeline.jsx currently has its own copies of these (they predate
// this tabs/ folder) — once the tab components are wired into the page, its
// copies should be dropped in favor of importing from here.

import {
  format,
  differenceInCalendarDays,
  formatDistanceToNowStrict,
  isTomorrow,
  isYesterday,
} from 'date-fns';
import {
  Sparkles,
  Send,
  Check,
  CalendarClock,
  Clock,
  CheckCircle2,
  Star,
  X,
  XCircle,
  UserX,
  AlertCircle,
  Info,
} from 'lucide-react';
import { CANDIDATE_MAPPING_STATUS_VALUES } from 'src/utils/statusConstants';

// "in_progress" → "In Progress" — fallback for any status the constants map
// doesn't cover.
export const humanizeStatus = (status) =>
  status
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

export const statusLabel = (status) =>
  CANDIDATE_MAPPING_STATUS_VALUES[status] || humanizeStatus(status);

// Background color for each real senior_level_flow status — powers the
// small filled status badge RowActionsCell.jsx pins to the top-right corner
// of its cell in a merged "All statuses" view (e.g. the Interview tab's Test
// In Progress/Interview In Progress/In Progress, or the Contact tab's
// Contacted/Acknowledged), in place of a dedicated Status column that only
// ever existed for that one view and ate into every other column's width
// while it was showing. Scoped to senior_level_flow's own status strings —
// the `default`/`secondary` flows still use the shared statusColumn
// (sharedColumns.js) and aren't affected by this map. Falls back to a
// neutral gray for anything missing here rather than erroring.
const SENIOR_FLOW_STATUS_SCHEME = {
  New: 'slate',
  Contacted: 'blue',
  Acknowledged: 'emerald',
  'Ready to Schedule': 'amber',
  'Test In Progress': 'indigo',
  'Interview In Progress': 'indigo',
  'In Progress': 'indigo',
  Completed: 'emerald',
  Shortlisted: 'emerald',
  Failed: 'red',
  Rejected: 'red',
  'Not Interested': 'red',
  'No Show': 'red',
  'Non Responsive': 'orange',
};

const SCHEME_BG_CLASS = {
  emerald: 'bg-ds-success',
  amber: 'bg-ds-warning',
  red: 'bg-ds-danger',
  teal: 'bg-teal-600',
  indigo: 'bg-indigo-600',
  violet: 'bg-violet-600',
  blue: 'bg-blue-600',
  orange: 'bg-orange-600',
  slate: 'bg-slate-500',
  gray: 'bg-gray-400',
};

export const statusBgClass = (status) =>
  SCHEME_BG_CLASS[SENIOR_FLOW_STATUS_SCHEME[status]] || SCHEME_BG_CLASS.gray;

// Icon for the same top-right status badge — one per real senior_level_flow
// status, so the badge reads at a glance even collapsed down to icon-only
// (see RowActionsCell.jsx's hover-to-expand treatment). Falls back to a
// plain Info glyph for anything missing here, same fallback spirit as
// statusBgClass's gray.
const SENIOR_FLOW_STATUS_ICON = {
  New: Sparkles,
  Contacted: Send,
  Acknowledged: Check,
  'Ready to Schedule': CalendarClock,
  'Test In Progress': Clock,
  'Interview In Progress': Clock,
  'In Progress': Clock,
  Completed: CheckCircle2,
  Shortlisted: Star,
  Failed: X,
  Rejected: X,
  'Not Interested': XCircle,
  'No Show': UserX,
  'Non Responsive': AlertCircle,
};

export const statusIcon = (status) => SENIOR_FLOW_STATUS_ICON[status] || Info;

// Cached off-screen canvas 2d context, reused across calls rather than
// created per-measurement — canvas creation is the expensive part, the
// measureText call itself is cheap.
let statusLabelMeasureCtx = null;
const getStatusLabelMeasureCtx = () => {
  if (statusLabelMeasureCtx) return statusLabelMeasureCtx;
  if (typeof document === 'undefined') return null;
  statusLabelMeasureCtx = document.createElement('canvas').getContext('2d');
  return statusLabelMeasureCtx;
};

// Exact rendered pixel width of a status label, in the same font the
// top-right status badge renders it in (RowActionsCell.jsx's `text-[8px]
// font-semibold`) — powers that badge's hover-expanded width. A `ch`-based
// guess (`label.length` characters) was tried first but `ch` is pegged to
// the "0" glyph's width, not this (proportional, Inter) font's real
// character widths, so it increasingly overshot on longer multi-word labels
// — confirmed live as a visible empty gap after "Interview In Progress".
// Measuring the actual string sidesteps that entirely. Falls back to the
// same rough per-character guess only if canvas is unavailable (SSR).
export const measureStatusLabelWidth = (text) => {
  const ctx = getStatusLabelMeasureCtx();
  if (!ctx) return text.length * 5;
  ctx.font = '600 8px Inter, ui-sans-serif, system-ui, sans-serif';
  return ctx.measureText(text).width;
};

// Ported from the old RequirementPipeline.jsx's parseDateSafely — the "default"
// flow's candidate payloads have shown up as ISO strings, numeric
// timestamps, and dd/mm/yyyy-ish strings, so plain `new Date(value)` isn't
// always enough.
const parseDateSafely = (value) => {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;

  if (typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const str = String(value).trim();
  if (!str) return null;

  let d = new Date(str);
  if (!Number.isNaN(d.getTime())) return d;

  const m = str.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})$/);
  if (m) {
    const day = Number(m[1]);
    const month = Number(m[2]);
    const year = Number(m[3].length === 2 ? `20${m[3]}` : m[3]);
    d = new Date(year, month - 1, day);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  return null;
};

export const formatDate = (value) => {
  const d = parseDateSafely(value);
  return d ? format(d, 'dd MMM yyyy') : '-';
};

// Whole calendar days from a date string to today — powers the Scheduling
// tab's "Waiting to Schedule" column (row.updated_at: when the candidate
// entered Ready to Schedule, per explicit instruction to rely on that field
// rather than mapped_at/created_at). Returns null for a missing/invalid
// date so the cell can render its own empty state; clamped to 0 so clock
// skew or a same-day update never shows a negative count.
export const daysSince = (value) => {
  const d = parseDateSafely(value);
  if (!d) return null;
  return Math.max(0, differenceInCalendarDays(new Date(), d));
};

// The inverse of daysSince — whole calendar days from today to a future
// date string. Powers the Contact tab's "Waiting" column's "Next in Nd"
// line (row.notifications.schedules' next not-yet-sent entry). Same
// null-for-missing/invalid-date contract as daysSince; clamped to 0 rather
// than going negative for a date that's today or already in the past.
export const daysUntil = (value) => {
  const d = parseDateSafely(value);
  if (!d) return null;
  return Math.max(0, differenceInCalendarDays(d, new Date()));
};

// A decimal count of years (the candidate-list API's `total_experience_
// years`, e.g. 4.6) → "4y 7m" / "7y" / "5m". Returns '' for null /
// undefined / 0 / non-numeric so callers can just `&&` on it.
export const formatExperienceYears = (years) => {
  const n = Number(years);
  if (!n || Number.isNaN(n) || n < 0) return '';
  let y = Math.floor(n);
  let m = Math.round((n - y) * 12);
  if (m === 12) {
    y += 1;
    m = 0;
  }
  if (y && m) return `${y}y ${m}m`;
  if (y) return `${y}y`;
  return `${m}m`;
};

// "3 Aug, 3:00 PM" — the Interview tab's own date format for
// interview_details.interview_time, distinct from formatDate's
// "dd MMM yyyy" (that one has no time-of-day, this column's whole point is
// the time). Returns '' for a missing/invalid date.
export const formatDateTime = (value) => {
  const d = parseDateSafely(value);
  return d ? format(d, 'd MMM, h:mm a') : '';
};

// Plain relative-time phrase off a single date — "in 5 days" / "2 days
// ago" / "tomorrow" / "yesterday". Powers the Interview tab's "When"
// column under the absolute date/time. Deliberately generic: no
// status-aware wording (e.g. an "interviewed X days ago" variant for an
// already-acknowledged past interview) — that would need a rule this
// column's real fields (interview_details) don't specify, so every past
// date reads the same plain way regardless of acknowledged/rejected state.
export const relativeTimeLabel = (value) => {
  const d = parseDateSafely(value);
  if (!d) return '';
  if (isTomorrow(d)) return 'tomorrow';
  if (isYesterday(d)) return 'yesterday';
  const isPast = d.getTime() < Date.now();
  const distance = formatDistanceToNowStrict(d);
  return isPast ? `${distance} ago` : `in ${distance}`;
};

// notice_period is a whole number of days — same vocabulary
// UpdateProfileDetailsModal's own NOTICE_OPTIONS use (0 / 15 / 30 / 60 /
// 90). null / undefined / non-numeric → the "not shared yet" copy the New
// tab's Location & Availability column shows in the screenshot.
export const formatNoticePeriod = (days) => {
  if (days == null || days === '') return 'Notice period not shared yet';
  const n = Number(days);
  if (Number.isNaN(n)) return 'Notice period not shared yet';
  if (n === 0) return 'Immediate joiner';
  return `Notice ${n} days`;
};
